import React from 'react';
import { imageUrl } from '../../../api/endpoints';
import { IconBalance } from '../../../components/icons/AnalyzerIcons';
import type { ScanCandidate } from '../../../types/scanV2';

/** Candidates shown per side (the server ranks them). */
export const MAX_CANDIDATES = 3;

export interface CandidateSheetProps {
  candidates: ScanCandidate[];
  /** The candidate being confirmed, if any (disables the sheet). */
  confirmingId?: string;
  onConfirm: (candidateId: string) => void;
  onNoneOfThese: () => void;
  /** Leave the list and go back to the side's entry. */
  onCancel: () => void;
  /** "Product A" / "Product B", for the cancel button's name. */
  label: string;
}

function candidateTitle(c: ScanCandidate): string {
  return [c.brand, c.name, c.variant].filter(Boolean).join(' ');
}

/** A calm hint on an AI guess the server could not back with web sources. */
function UnverifiedNote() {
  return (
    <>
      <span className="pill" style={{ marginTop: 'var(--s-1)' }}>Unverified match</span>
      <span className="t-small t-mute" style={{ display: 'block', marginTop: 'var(--s-1)' }}>
        We couldn&apos;t find this barcode online. Check the name before you confirm.
      </span>
    </>
  );
}

/** Up to three candidate cards, "None of these" and Cancel (ProductConfirmationDialog look). */
export default function CandidateSheet({ candidates, confirmingId, onConfirm, onNoneOfThese, onCancel, label }: CandidateSheetProps) {
  const busy = confirmingId !== undefined;
  return (
    <div className="stack-3" data-testid="candidate-sheet">
      <p className="t-small" style={{ margin: 0, fontWeight: 600, color: 'var(--ink)' }}>Is it one of these?</p>
      {candidates.slice(0, MAX_CANDIDATES).map((c) => {
        const title = candidateTitle(c);
        const src = c.image_url ? imageUrl(c.image_url) : null;
        const confirming = confirmingId === c.candidate_id;
        return (
          <button
            key={c.candidate_id}
            type="button"
            className="card card--bordered"
            style={{ padding: 'var(--s-3)', display: 'flex', alignItems: 'center', gap: 'var(--s-3)', textAlign: 'left' }}
            disabled={busy}
            onClick={() => { onConfirm(c.candidate_id); }}
            aria-label={`Use ${title}`}
            aria-busy={confirming}
          >
            <span
              style={{
                width: 48, height: 48, flexShrink: 0, borderRadius: 'var(--r-sm)', overflow: 'hidden',
                background: 'var(--c-cream-weak)', display: 'grid', placeItems: 'center',
              }}
            >
              {src ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <IconBalance />}
            </span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span className="t-small t-mute" style={{ display: 'block' }}>{c.brand}</span>
              <span className="t-small" style={{ display: 'block', fontWeight: 600, color: 'var(--ink)' }}>
                {[c.name, c.variant].filter(Boolean).join(' · ')}
              </span>
              {c.source === 'ai_unverified' ? <UnverifiedNote /> : null}
            </span>
            <span className="pill pill--teal">{confirming ? 'Checking…' : 'This one'}</span>
          </button>
        );
      })}
      <button type="button" className="btn btn--ghost btn--sm justify-center" disabled={busy} onClick={onNoneOfThese}>
        None of these
      </button>
      <button
        type="button"
        className="btn btn--ghost btn--sm justify-center"
        disabled={busy}
        onClick={onCancel}
        aria-label={`Cancel and choose another way for ${label}`}
      >
        Cancel
      </button>
    </div>
  );
}
